import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { statusToneIconClass, type StatusToneLike } from '@/lib/statusTone';
import { Icon, type IconName } from '@/components/ui/icon';

interface PanelProps {
  /** Panel header title */
  title?: string;
  /** Optional count to display in a badge next to title */
  count?: number;
  /** Optional subtitle/description */
  description?: string;
  /** Icon to display before title */
  icon?: IconName;
  /** Optional icon tone */
  iconTone?: StatusToneLike;
  /** Actions to display in header */
  actions?: ReactNode;
  /** Panel content */
  children: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /** Additional class names for footer wrapper */
  footerClassName?: string;
  /** Size variant */
  size?: 'compact' | 'default' | 'prominent';
  /** Additional class names for root element */
  className?: string;
  /** Additional class names for content wrapper */
  contentClassName?: string;
}

/**
 * Presentational content container with optional header/footer.
 *
 * Replaces inline `rounded-lg border border-[var(--border-default)] bg-surface p-6 shadow-sm` patterns.
 * Use for company registry info, static metadata, or grouping related content.
 *
 * Visual treatment:
 * - shadow-sm (subtle elevation)
 * - border-default
 * - No blue accent border (use NavigableEntityCard for navigation)
 * - No hover effects
 *
 * @example
 * ```tsx
 * <Panel title="Informazioni generali" icon="info">
 *   <DataList items={[
 *     { label: "P.IVA", value: company.vat },
 *     { label: "Codice fiscale", value: company.fiscalCode },
 *   ]} />
 * </Panel>
 *
 * <Panel
 *   title="Notes"
 *   actions={<Button size="sm">Edit</Button>}
 *   footer={<Button className="w-full">Save</Button>}
 * >
 *   <Textarea value={notes} onChange={...} />
 * </Panel>
 * ```
 */
export function Panel({
  title,
  count,
  description,
  icon,
  iconTone,
  actions,
  children,
  footer,
  footerClassName,
  size = 'default',
  className,
  contentClassName,
}: PanelProps) {
  const sizeConfig = {
    compact: {
      root: 'shadow-sm',
      header: 'p-4 pb-3',
      content: 'p-4',
      footer: 'p-4 pt-3',
      title: 'text-xs',
    },
    default: {
      root: 'shadow-sm',
      header: 'p-6 pb-4',
      content: 'p-6',
      footer: 'p-6 pt-4',
      title: 'text-sm',
    },
    prominent: {
      root: 'shadow-md',
      header: 'p-7 pb-5',
      content: 'p-7',
      footer: 'p-7 pt-5',
      title: 'text-sm',
    },
  }[size];

  return (
    <div
      className={cn(
        'rounded-lg border border-[var(--border-default)] bg-surface',
        sizeConfig.root,
        className
      )}
    >
      {(title || actions) && (
        <div
          className={cn(
            'flex items-start justify-between gap-4 border-b border-[var(--border-subtle)]',
            sizeConfig.header
          )}
        >
          <div className="space-y-1">
            {title && (
              <div className="flex items-center gap-2">
                {icon && (
                  <Icon
                    name={icon}
                    className={cn(
                      'text-xl',
                      iconTone ? statusToneIconClass(iconTone) : 'text-brand-blue-500'
                    )}
                  />
                )}
                <h3
                  className={cn(
                    'font-semibold uppercase tracking-wide text-text-secondary',
                    sizeConfig.title
                  )}
                >
                  {title}
                </h3>
                {count !== undefined && (
                  <span className="inline-flex items-center justify-center rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-text-secondary">
                    {count}
                  </span>
                )}
              </div>
            )}
            {description && <p className="text-sm text-text-secondary">{description}</p>}
          </div>
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </div>
      )}
      <div className={cn(sizeConfig.content, contentClassName)}>{children}</div>
      {footer && (
        <div
          className={cn(
            'border-t border-[var(--border-subtle)]',
            sizeConfig.footer,
            footerClassName
          )}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
